import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import LessonVideoSource from '../course/LessonVideoSource';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin card: configure the first-login welcome modal (video + training course).
export default function OnboardingSettingsCard({ getAuthHeader }) {
  const [videoUrl, setVideoUrl] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courses, setCourses] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [autoNudge, setAutoNudge] = useState(false);
  const [stallDays, setStallDays] = useState(3);
  const [lastRun, setLastRun] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          axios.get(`${API_URL}/api/admin/onboarding-settings`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/courses`, { headers: getAuthHeader() }),
        ]);
        setVideoUrl(s.data.video_url || '');
        setCourseId(s.data.training_course_id || '');
        setResolved(s.data.resolved_course || null);
        setAutoNudge(!!s.data.auto_nudge_enabled);
        setStallDays(s.data.auto_nudge_stall_days || 3);
        setLastRun(s.data.auto_nudge_last_run
          ? { at: s.data.auto_nudge_last_run, ...(s.data.auto_nudge_last_result || {}) }
          : null);
        setCourses(c.data || []);
      } catch {
        toast.error('Could not load onboarding settings');
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/onboarding-settings`,
        { video_url: videoUrl || null, training_course_id: courseId || null,
          auto_nudge_enabled: autoNudge, auto_nudge_stall_days: Number(stallDays) || 3 },
        { headers: getAuthHeader() },
      );
      toast.success('Onboarding settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wm-card p-6 rounded-lg border border-border space-y-4" data-testid="onboarding-settings-card">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Founder Onboarding (Welcome Modal)</h3>
        <p className="text-sm text-muted-foreground mt-1">
          New accounts see a welcome popup on first login with a video and a 4-step setup checklist.
          Configure the welcome video and which course the &ldquo;Start lesson&rdquo; button opens.
        </p>
      </div>

      {!loaded ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="space-y-2">
            <Label className="text-foreground">Welcome video</Label>
            <LessonVideoSource value={videoUrl} onChange={setVideoUrl} getAuthHeader={getAuthHeader} />
          </div>

          <div className="space-y-2">
            <Label className="text-foreground">Training course (Step 4 &ldquo;Start lesson&rdquo;)</Label>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className="w-full bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground"
              data-testid="onboarding-course-select"
            >
              <option value="">
                Auto{resolved ? ` — ${resolved.title}` : ' (matches "sales training")'}
              </option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-md border border-border p-4 space-y-3" data-testid="onboarding-auto-nudge-section">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label className="text-foreground">Automatic reminder emails</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Checks daily and emails members who stalled on their Get Started checklist.
                  Max 3 automatic reminders per member, at least a week apart — stops the moment they finish.
                </p>
              </div>
              <Switch
                checked={autoNudge}
                onCheckedChange={setAutoNudge}
                data-testid="onboarding-auto-nudge-toggle"
              />
            </div>
            {autoNudge && (
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Remind after</Label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={stallDays}
                  onChange={(e) => setStallDays(e.target.value)}
                  className="w-20 bg-input border border-border rounded-md px-2 py-1 text-sm text-foreground"
                  data-testid="onboarding-auto-nudge-days"
                />
                <span className="text-sm text-muted-foreground">days without finishing</span>
              </div>
            )}
            {lastRun && (
              <p className="text-xs text-muted-foreground" data-testid="onboarding-auto-nudge-last-run">
                Last automatic run: {new Date(lastRun.at).toLocaleString()} — {lastRun.sent ?? 0} sent
                {lastRun.skipped_recent ? `, ${lastRun.skipped_recent} skipped (recently nudged)` : ''}
                {lastRun.failed ? `, ${lastRun.failed} failed` : ''}
              </p>
            )}
          </div>

          <Button
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="onboarding-settings-save"
          >
            {saving ? 'Saving…' : 'Save onboarding settings'}
          </Button>
        </>
      )}
    </div>
  );
}
